import { FC, useEffect } from "react";
import { Button, Dialog, Flex, Spinner, Stack } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import { CaseDocumentModel } from "@shared/models";
import { selectors } from "@containers/Cases/store";
import { selectors as authSelectors } from "@containers/Auth/store";
import { deleteCaseFile, getCaseDocuments, getMedicalRecordsResult } from "@containers/Cases/store/actions";

import { CaseDocumentDoneIcon, CaseDocumentFailedFileRow, CaseDocumentModalHeader } from "../CaseDocumentModal";
import { isFailedFile } from "../CaseDocuments.utils";
import MedicalRecordsFieldResult from "./MedicalRecordsFieldResult";

const getSubtitle = ({ files }: CaseDocumentModel, failedFiles: number) =>
  failedFiles
    ? `${files.length - failedFiles} of ${files.length} files were read. Extracted text was saved; review anything flagged.`
    : "Extracted text was saved. Review anything flagged below.";

interface ResultFooterProps {
  pendingChoices: number;
  onClose: () => void;
  onChooseValues?: () => void;
}

const ResultFooter: FC<ResultFooterProps> = ({ pendingChoices, onClose, onChooseValues }) => (
  <Dialog.Footer gap={3} mt={2} borderTop="1px solid" borderColor="gray.200">
    {pendingChoices > 0 ? (
      <>
        <Button variant="neutral" fontSize="14px" onClick={onClose}>
          Decide later
        </Button>
        {onChooseValues && (
          <Button variant="primary" fontSize="14px" onClick={onChooseValues}>
            Choose values ({pendingChoices})
          </Button>
        )}
      </>
    ) : (
      <Button variant="primary" fontSize="14px" onClick={onClose}>
        Done
      </Button>
    )}
  </Dialog.Footer>
);

interface MedicalRecordsResultStepProps {
  caseId: number;
  caseDocument: CaseDocumentModel;
  onClose: () => void;
  onReplaceFile: () => void;
  /** Opens the Choose values window; left out when the user can't choose */
  onChooseValues?: () => void;
}

const MedicalRecordsResultStep: FC<MedicalRecordsResultStepProps> = ({
  caseId,
  caseDocument,
  onClose,
  onReplaceFile,
  onChooseValues,
}) => {
  const dispatch = useDispatch();
  const fields = useSelector(selectors.getMedicalRecordsResult());
  // Changing case documents needs the update_case permission, which only admins have.
  const canChangeDocuments = !!useSelector(authSelectors.isAdmin());

  const failedFiles = caseDocument.files.filter(isFailedFile);

  useEffect(() => {
    if (!fields) dispatch(getMedicalRecordsResult.request(caseId));
  }, [caseId, dispatch, fields]);

  const deleteFile = (fileId: number) => {
    dispatch(deleteCaseFile.request({ caseId, fileId, cb: () => dispatch(getCaseDocuments.request(caseId)) }));
  };

  return (
    <>
      <CaseDocumentModalHeader
        title="Medical summaries added"
        subtitle={getSubtitle(caseDocument, failedFiles.length)}
        icon={<CaseDocumentDoneIcon />}
      />
      <Dialog.Body display="flex" flexDirection="column" gap={4}>
        {failedFiles.length > 0 && (
          <Stack gap={2}>
            {failedFiles.map((file) => (
              <CaseDocumentFailedFileRow
                key={file.id}
                file={file}
                title="Couldn't read this file"
                reason={[file.error_message, "The other files were saved."].filter(Boolean).join(" ")}
                onReplace={canChangeDocuments ? onReplaceFile : undefined}
                onRemove={canChangeDocuments ? () => deleteFile(file.id) : undefined}
              />
            ))}
          </Stack>
        )}

        {fields ? (
          <Flex direction="column" border="1px solid" borderColor="gray.200" borderRadius="10px">
            {fields.map((result) => (
              <MedicalRecordsFieldResult key={result.field} result={result} />
            ))}
          </Flex>
        ) : (
          <Flex justify="center" py={6}>
            <Spinner color="brand.emptyStateText" />
          </Flex>
        )}
      </Dialog.Body>
      <ResultFooter pendingChoices={caseDocument.pending_choices} onClose={onClose} onChooseValues={onChooseValues} />
    </>
  );
};

export default MedicalRecordsResultStep;
